import { useEffect, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Heart, Brain, Zap, Wind, Meh } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const STORAGE_KEY = 'zenscroll_mood_lastShownDay';
export const MOOD_EVENT = 'zenscroll:mood-survey';

const OPTIONS = [
  { id: 'inspired',  label: 'Inspired',          Icon: Sparkles, color: '#FFD166' },
  { id: 'relaxed',   label: 'Relaxed',           Icon: Wind,     color: '#1C4749' },
  { id: 'motivated', label: 'Motivated',         Icon: Zap,      color: '#FFD166' },
  { id: 'learned',   label: 'Learned something', Icon: Brain,    color: '#1C4749' },
  { id: 'curious',   label: 'Curious',           Icon: Heart,    color: '#FFD166' },
  { id: 'meh',       label: 'Just okay',         Icon: Meh,      color: '#94A3B8' },
];

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Logout-only mood survey. Mounted globally; opens when another part of the
 * app dispatches `window.dispatchEvent(new CustomEvent('zenscroll:mood-survey',
 * { detail: { onDone } }))`. If the user has already responded today, the
 * onDone callback fires immediately and the modal stays closed.
 */
export default function MoodExitModal() {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const onDoneRef = useRef(null);

  const runDone = useCallback(() => {
    const cb = onDoneRef.current;
    onDoneRef.current = null;
    if (typeof cb === 'function') cb();
  }, []);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(STORAGE_KEY, todayKey()); } catch { /* ignore */ }
    setOpen(false);
    runDone();
  }, [runDone]);

  const submit = useCallback(async (moodId) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('zenscroll_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/feedback/mood`, { mood: moodId, context: 'logout' }, { headers });
      toast.success("Thanks — we'll keep tuning the feed for you.", { duration: 1800 });
    } catch {
      // best-effort: never block the user from signing out
    }
    setSubmitting(false);
    dismiss();
  }, [submitting, dismiss]);

  useEffect(() => {
    const handler = (e) => {
      const onDone = e?.detail?.onDone;
      let lastShown = '';
      try { lastShown = localStorage.getItem(STORAGE_KEY) || ''; } catch { /* ignore */ }
      if (lastShown === todayKey()) {
        // Already answered today — fire the callback immediately so logout can
        // proceed without interruption.
        if (typeof onDone === 'function') onDone();
        return;
      }
      onDoneRef.current = onDone || null;
      setOpen(true);
    };
    window.addEventListener(MOOD_EVENT, handler);
    return () => window.removeEventListener(MOOD_EVENT, handler);
  }, []);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-md flex items-end sm:items-center justify-center p-4"
          data-testid="mood-exit-modal"
          onClick={dismiss}
        >
          <motion.div
            initial={{ y: 40, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-[#101F1D] border border-white/10 rounded-3xl p-7 shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
          >
            <button
              onClick={dismiss}
              data-testid="mood-exit-close"
              aria-label="Dismiss"
              className="absolute top-4 right-4 p-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/5 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            <p className="text-[10px] uppercase tracking-[0.22em] text-[#FFD166] font-semibold">Quick check-in</p>
            <h3 className="mt-2 text-2xl text-white tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Before you go —<br />how did ZenScroll make you feel?
            </h3>
            <p className="mt-2 text-sm text-[#94A3B8]">
              Your answer helps us shape a feed that actually leaves you better than it found you.
            </p>

            <div className="mt-6 grid grid-cols-2 gap-2.5">
              {OPTIONS.map(({ id, label, Icon, color }) => (
                <button
                  key={id}
                  onClick={() => submit(id)}
                  disabled={submitting}
                  data-testid={`mood-option-${id}`}
                  className="flex items-center gap-2.5 px-4 py-3 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all text-left disabled:opacity-50"
                >
                  <span
                    className="inline-flex items-center justify-center w-8 h-8 rounded-full"
                    style={{ backgroundColor: `${color}22`, color }}
                  >
                    <Icon className="w-4 h-4" />
                  </span>
                  <span className="text-sm text-white">{label}</span>
                </button>
              ))}
            </div>

            <button
              onClick={dismiss}
              data-testid="mood-skip-btn"
              className="mt-4 w-full text-xs text-white/40 hover:text-white/70 transition-colors py-2"
            >
              Skip
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
